import { useEffect } from "react";
import { ctaEventForLink, PLACEMENT_ATTRIBUTE } from "@/lib/ctaEvents";
import { track } from "@/lib/track";

/**
 * One delegated click listener for the funnel CTAs — sign up, login and the
 * demo — instead of an onClick at each of the ~25 call sites. Links are
 * recognised by where they point (see `ctaEventForLink`), so a new CTA built
 * with `signupUrl()` is tracked without anyone remembering to wire it up.
 *
 * `track` is a no-op until analytics consent is given.
 */
export function CtaClickTracker() {
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const cta = ctaEventForLink(
        {
          href: anchor.href,
          target: anchor.target,
          placement: anchor.closest(`[${PLACEMENT_ATTRIBUTE}]`)?.getAttribute(PLACEMENT_ATTRIBUTE),
        },
        window.location
      );
      if (!cta) return;

      // A modified click opens a new tab, so this page stays alive.
      const newTab = event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1;
      track(cta.event, cta.properties, { beacon: cta.leavesPage && !newTab });
    }

    // Capture phase: runs before React Router's handler navigates away.
    document.addEventListener("click", handleClick, true);
    document.addEventListener("auxclick", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("auxclick", handleClick, true);
    };
  }, []);

  return null;
}
